import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentCompany } from "./useCurrentCompany";
import { SUPPORTED_LOCALES, type SupportedLocale } from "@/i18n";

const STORAGE_KEY = "lovable.lng";

/**
 * Resolves the active interface locale for the current user, persists it,
 * and exposes the list of locales the user is allowed to choose from.
 *
 * Choice rules:
 *  - English is always available.
 *  - Locales enabled by the user's active company are available.
 *  - The user's previously chosen locale is always available (so a German
 *    speaker in a Dutch-only tenant can still see the UI in German; dynamic
 *    DB content will simply fall back to English).
 */
export function useLocale() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { id: companyId } = useCurrentCompany();
  const [companyLocales, setCompanyLocales] = useState<SupportedLocale[]>([]);

  // Load company-enabled locales
  useEffect(() => {
    let cancelled = false;
    if (!companyId) {
      setCompanyLocales([]);
      return;
    }
    supabase
      .from("company_locales")
      .select("locale")
      .eq("company_id", companyId)
      .then(({ data }) => {
        if (cancelled) return;
        const list = (data ?? [])
          .map((r) => r.locale as SupportedLocale)
          .filter((l) => (SUPPORTED_LOCALES as readonly string[]).includes(l));
        setCompanyLocales(list);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Hydrate from saved user preference once authenticated
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("user_preferences")
      .select("locale")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const saved = data?.locale as SupportedLocale | undefined;
        if (saved && saved !== i18n.language) {
          i18n.changeLanguage(saved);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user, i18n]);

  const setLocale = useCallback(
    async (lng: SupportedLocale) => {
      await i18n.changeLanguage(lng);
      try {
        localStorage.setItem(STORAGE_KEY, lng);
      } catch {
        /* no-op */
      }
      if (user) {
        await supabase
          .from("user_preferences")
          .upsert({ user_id: user.id, locale: lng, updated_at: new Date().toISOString() });
      }
    },
    [i18n, user],
  );

  // The set of locales the switcher should show.
  const current = (i18n.language?.split("-")[0] ?? "en") as SupportedLocale;
  const availableSet = new Set<SupportedLocale>(["en", current, ...companyLocales]);
  const available = (SUPPORTED_LOCALES as readonly SupportedLocale[]).filter((l) =>
    availableSet.has(l),
  );

  return {
    current,
    available,
    companyLocales,
    setLocale,
  };
}
